import type { PlantRole, RecipePlant } from "./types";

/**
 * Thriller / filler / spiller for each recipe plant.
 *
 * The recipe's own `plant_roles` win. Gaps are inferred from the alias's AI
 * properties ("Typically 18-30 inches tall…", "low, creeping, trailing…") so
 * an untagged recipe still lays out. Inference is deterministic: same text,
 * same role.
 */

const TRAILING =
  /\b(trail\w*|cascad\w*|spill\w*|creep\w*|mat-forming|groundcover|ground cover|vining|scrambl\w*)\b/i;

/** Largest height mentioned in free text, in inches (null when none parse). */
export function maxHeightInches(text?: string | null): number | null {
  if (!text) return null;
  let best: number | null = null;
  const re =
    /(\d+(?:\.\d+)?)\s*(?:(?:-|–|to)\s*(\d+(?:\.\d+)?))?\s*(feet|foot|ft|'|inches|inch|in\b|")/gi;
  for (const m of text.matchAll(re)) {
    const hi = parseFloat(m[2] ?? m[1]);
    const unit = m[3].toLowerCase();
    const inches = unit.startsWith("f") || unit === "'" ? hi * 12 : hi;
    if (!Number.isFinite(inches)) continue;
    best = best === null ? inches : Math.max(best, inches);
  }
  return best;
}

function heightOf(p: RecipePlant): number {
  // "tall" sentence first: dimensions often read "6-12 in tall and 12-18 in wide".
  const tall = p.dimensions?.split(/\band\b|,|;/i)[0];
  return (
    maxHeightInches(tall) ??
    maxHeightInches(p.matureHeight) ??
    maxHeightInches(p.dimensions) ??
    12
  );
}

function isTrailing(p: RecipePlant): boolean {
  return TRAILING.test(`${p.physics ?? ""} ${p.dimensions ?? ""}`);
}

export function resolveRoles(
  plants: RecipePlant[],
  authored: Record<string, PlantRole> | null | undefined
): Record<string, PlantRole> {
  const roles: Record<string, PlantRole> = {};
  for (const p of plants) {
    const a = authored?.[p.id];
    if (a) roles[p.id] = a;
    else if (isTrailing(p) && heightOf(p) <= 12) roles[p.id] = "spiller";
  }
  const open = plants.filter((p) => !roles[p.id]);
  const hasThriller = plants.some((p) => roles[p.id] === "thriller");
  if (!hasThriller && open.length) {
    // The tallest untagged plant is the thriller; ties go to recipe order.
    const tallest = open.reduce((a, b) => (heightOf(b) > heightOf(a) ? b : a));
    roles[tallest.id] = "thriller";
  }
  for (const p of plants) if (!roles[p.id]) roles[p.id] = "filler";
  // A design needs a centerpiece: promote the tallest filler when every plant
  // was authored/inferred as a spiller.
  if (!Object.values(roles).includes("thriller") && plants.length) {
    const tallest = plants.reduce((a, b) =>
      heightOf(b) > heightOf(a) ? b : a
    );
    roles[tallest.id] = "thriller";
  }
  return roles;
}

const ROLE_ORDER: Record<PlantRole, number> = {
  thriller: 0,
  filler: 1,
  spiller: 2,
};

/** Diagram order: thrillers, then fillers, then spillers; recipe order within a role. */
export function orderByRole<T extends { id: string }>(
  plants: T[],
  roles: Record<string, PlantRole>
): T[] {
  return plants
    .map((p, i) => ({ p, i }))
    .sort(
      (a, b) =>
        ROLE_ORDER[roles[a.p.id]] - ROLE_ORDER[roles[b.p.id]] || a.i - b.i
    )
    .map(({ p }) => p);
}

/**
 * Roles the recipe has that the shopper has unchecked entirely (PLA2-970),
 * each with the recipe's first plant of that role as a suggestion. Only roles
 * the recipe actually has are reported, thriller → filler → spiller.
 */
export function missingRoles(
  plants: readonly { id: string; name: string }[],
  roles: Record<string, PlantRole>,
  keptIds: readonly string[]
): { role: PlantRole; suggestion: string }[] {
  const kept = new Set(keptIds);
  const out: { role: PlantRole; suggestion: string }[] = [];
  for (const role of ["thriller", "filler", "spiller"] as PlantRole[]) {
    const ofRole = plants.filter((p) => roles[p.id] === role);
    if (ofRole.length && !ofRole.some((p) => kept.has(p.id)))
      out.push({ role, suggestion: ofRole[0].name });
  }
  return out;
}
